<template>
  <div>
    <div v-if="loading || forceLoading" class="d-flex mt-5">
      <v-progress-circular class="ma-auto" indeterminate color="primary" />
    </div>

    <div class="d-flex flex-column gap-4">
      <OperationPriorityChip
        v-if="computedFastTrack || canUpdateTags"
        :priority="computedFastTrack"
        :z-index="zIndex"
        :read-only="!canUpdateTags"
        @change-priority="
          (priority) => updateOperation(op, {op_fast_track: priority})
        "
      />

      <OperationTags
        v-if="op.op_tags?.length || canUpdateTags"
        :operation="{tags: op.op_tags}"
        :z-index="zIndex"
        hide-status
        :removable="canUpdateTags"
        :addable="canUpdateTags"
        :tags-from-import="op.import_op_tags"
        @add="onOpTagAdd"
        @remove="onOpTagRemove"
      />
    </div>

    <div
      class="flux-data-body__info-section text-14 text-neutral-tertiary mt-4"
    >
      <div class="flux-data-body__info-block">
        <div class="flux-data-body__info-header">
          <div class="flux-data-body__info-header-title">
            <OpalIcon name="hexagon" size="16" />

            <span>
              {{ $t("scheduling.sidebar__op_data_body.info_sector") }}
            </span>
          </div>

          <div>
            <span
              v-if="op.secteur_id && computedMachineCenter"
              class="text-neutral-secondary"
            >
              {{ computedMachineCenter.secteur_name || "-" }}
            </span>
            <span v-else class="text-neutral-secondary">
              {{ $t("scheduling.no_associated_sector") }}
            </span>

            <span
              v-if="op.ops_w_same_sequence"
              class="ml-2 text-neutral-tertiary"
            >
              {{
                $t("scheduling.op_on_multiple_sectors", {
                  count: op.ops_w_same_sequence,
                })
              }}
            </span>
          </div>
        </div>

        <SectorsSelector
          :class="CSS_OPERATION_CARD_CLICK_OUTSIDE_CLASS"
          :disabled="!canUpdateSector"
          :selected-machine-prop="computedMachineCenter"
          :z-index="zIndex"
          :data-testid="TEST_IDS.FLUX_DATA_BODY__SECTORS_SELECTOR"
          :can-select-all="false"
          :can-select-multiple="false"
          :title="$t('scheduling.modify_sector_title')"
          content-class="force-max-height"
          use-simple-update-event-in-modal
          hide-reset-button
          open-first-selected
          @update-sector="setMachineCenter"
        >
          <template #activator="props">
            <FButton
              v-bind="props"
              :disabled="!canUpdateSector"
              class="semi-bold"
              icon="edit-2"
              small
              transparent
              is-opal-icon
            >
              {{ $t("scheduling.sidebar__op_data_body.action_modify") }}
            </FButton>
          </template>
        </SectorsSelector>
      </div>

      <div class="flux-data-body__info-block">
        <div class="flux-data-body__info-header">
          <div class="flux-data-body__info-header-title">
            <OpalIcon name="calendar" size="16" />

            <span>
              {{ $t("scheduling.sidebar__op_data_body.info_date") }}
            </span>
          </div>

          <span
            class="text-neutral-secondary"
            :data-testid="TEST_IDS.FLUX_DATA_BODY__OPERATION_DATE"
          >
            {{ displayedDate }}
          </span>
        </div>

        <FDatePicker
          :date-value="computedDate"
          :field-value="selectedDate"
          :disabled="!canUpdateDate"
          :handle-change="handleDateChange"
          can-select-shift
          @validate="handleDateValidation"
        >
          <template #activator="{toggleDatePicker}">
            <FButton
              :disabled="!canUpdateDate"
              class="semi-bold"
              icon="edit-2"
              small
              transparent
              is-opal-icon
              @click="() => toggleDatePicker()"
            >
              {{ $t("scheduling.sidebar__op_data_body.action_modify") }}
            </FButton>
          </template>
        </FDatePicker>
      </div>

      <div class="flux-data-body__info-block">
        <div class="flux-data-body__info-header">
          <div class="flux-data-body__info-header-title">
            <OpalIcon name="clock" size="16" />

            <span>
              {{ $t("scheduling.sidebar__op_data_body.info_duration") }}
            </span>
          </div>

          <span class="text-neutral-secondary">
            {{ displayedOPDuration }}
          </span>
        </div>

        <v-menu
          :disabled="!canUpdateTags"
          :z-index="zIndex"
          location="bottom"
          offset="8"
        >
          <template v-slot:activator="{props}">
            <v-chip
              v-bind="props"
              :color="computedFault ? 'newPinkRegular' : 'newMainText'"
              :disabled="!canUpdateTags"
              class="px-4 rounded-pill cursor-pointer incident-chip text-14"
              size="small"
              pill
              data-testid="incident-chip"
            >
              <v-img
                v-if="!['none', ''].includes(computedFault)"
                :src="getAssetURL('icons', `${computedFault}.svg`)"
                width="14"
                class="mr-2"
                cover
              />
              <span data-testid="incident-chip-label">
                {{ $t(`operation.faults.${computedFault || "none"}`) }}
              </span>
              <vue-feather type="edit-2" tag="div" class="ml-2" size="12" />
            </v-chip>
          </template>
          <div :class="CSS_OPERATION_CARD_CLICK_OUTSIDE_CLASS">
            <v-chip
              v-for="f in faults"
              :key="f"
              :color="f === 'none' ? 'newMainText' : 'newPinkRegular'"
              class="ma-2 px-2 cursor-pointer d-block incident-dropdown-chip text-14"
              size="small"
              @click="setFault(f)"
              :data-testid="`incident-chip-${f}`"
            >
              <v-img
                v-if="f !== 'none'"
                :src="getAssetURL('icons', `${f}.svg`)"
                width="14"
                class="mr-2"
                cover
              />
              {{ $t("operation.faults." + f) }}
            </v-chip>
          </div>
        </v-menu>
      </div>
    </div>

    <div
      class="flux-data-body__quantity-section text-14 text-neutral-secondary mt-4"
    >
      <span class="semi-bold">
        {{ $t("scheduling.sidebar__op_data_body.quantities") }}
      </span>

      <div class="flux-data-body__quantity-row">
        <span class="flux-data-body__quantity-row-header">
          {{ $t("scheduling.sidebar__op_data_body.quantity_total") }}
        </span>

        <span>
          {{ op.quantite_of || 0 }}
        </span>
      </div>

      <div class="flux-data-body__quantity-row">
        <div class="flux-data-body__quantity-row-header">
          <OpalIcon
            name="box-checked"
            size="16"
            class="fg-neutral-tertiary"
            stroke="inherit"
          />

          <span>
            {{ $t("scheduling.sidebar__op_data_body.quantity_produced") }}
          </span>
        </div>

        <span
          v-if="clientParameters.enable_partial_op_validation"
          class="text-success-primary"
        >
          {{ op.quantity_produced || 0 }}
        </span>
        <FTextField
          v-else
          :model-value="computedProduced"
          :disabled="
            !canMakeChange ||
            isDisabledQuantityProduced ||
            clientParameters.enable_partial_op_validation
          "
          class="flux-data-body__quantity-row-input text-14"
          height="24px"
          width="64px"
          dense
          outlined
          @change="setQttyProduced"
        />
      </div>

      <div
        v-if="clientParameters.enable_partial_op_validation"
        class="flux-data-body__quantity-row"
      >
        <div class="flux-data-body__quantity-row-header">
          <OpalIcon
            name="box-crossed"
            size="16"
            class="fg-neutral-tertiary"
            stroke="inherit"
          />

          <span>
            {{ $t("scheduling.sidebar__op_data_body.quantity_reject") }}
          </span>
        </div>

        <span>
          {{ op.quantite_op_rebut || 0 }}
        </span>
      </div>

      <div
        v-if="clientParameters.enable_partial_op_validation"
        class="flux-data-body__quantity-row semi-bold"
      >
        <div class="flux-data-body__quantity-row-header">
          <OpalIcon
            name="box-play"
            size="16"
            class="fg-neutral-tertiary"
            stroke="inherit"
          />

          <span>
            {{ $t("scheduling.sidebar__op_data_body.quantity_on_going") }}
          </span>
        </div>

        <span>
          {{ op.quantite_of_disponible || 0 }}
        </span>
      </div>
    </div>

    <div
      v-if="clientParameters.enable_partial_op_validation"
      class="mt-4 w-100"
    >
      <FButton
        :outlined="op.op_status === OF_STATUS.DONE"
        :light="op.op_status === OF_STATUS.DONE"
        :filled="op.op_status !== OF_STATUS.DONE"
        :disabled="forceLoading"
        class="w-100"
        large
        @click="() => handleContextualAction()"
      >
        {{ operationActionLabel }}
      </FButton>
    </div>

    <OPValidationModal
      v-if="showValidationModal"
      :op="op"
      :next-op="nextOp"
      :is-loading="loading || forceLoading"
      :toggle-modal="() => (showValidationModal = false)"
      @validate-op="(event) => setQuantities(event)"
    />
  </div>
</template>

<script lang="ts">
import _ from "lodash";
import moment from "moment";
import numeral from "numeral";
import {storeToRefs} from "pinia";
import {
  PropType,
  defineComponent,
  ref,
  toRefs,
  computed,
  watch,
  inject,
} from "vue";
import {useI18n} from "vue-i18n";

import {
  includeOriginalUpdateShiftPlanningInSiblingDate,
  periodeToText,
  pgOpsMapFn,
  getOFUnit,
  isDoneOperation,
  getShiftFromDate,
  SchedulingTag,
  getMergedSchedulingOp,
} from "@oplit/shared-module";
import FDatePicker from "@/components/Global/FDatePicker.vue";
import SectorsSelector from "@/domains/scheduling/components/controls/SectorsSelector.vue";
import FButton from "@/lib/components/ui/FButton.vue";
import FTextField from "@/lib/components/ui/FTextField.vue";
import OPValidationModal from "@/domains/scheduling/domains/of-sidebar/components/OPValidationModal.vue";
import {usePermissionsStore} from "@/lib/stores/permissionsStore";
import {useMainStore} from "@/stores/mainStore";
import {useSchedulingStore} from "@/stores/schedulingStore";
import {useParametersStore} from "@/domains/parameters/stores/parametersStore";
import {useOperationStatus} from "@/composables/scheduling/useOperationStatus";
import useComputeQuantityUnit from "@/composables/useComputeQuantityUnit";
import {getAssetURL} from "@/tscript/utils/generalHelpers";
import {
  schedulingStatusColorText,
  getOperationQuantity,
} from "@/tscript/utils/schedulingUtils";
import {useGlobalTabs} from "@/composables/useGlobalTabs";
import {CSS_OPERATION_CARD_CLICK_OUTSIDE_CLASS} from "@/config/constants";
import {OF_STATUS, TEST_IDS} from "@/config/constants";
import {MachineCenter, OpenSnackbarFunction} from "@/interfaces";
import {SchedulingOperation} from "@/interfaces";
import OperationPriorityChip from "@/domains/scheduling/components/operations/OperationPriorityChip.vue";
import OperationTags from "@/domains/scheduling/components/operations/OperationTags.vue";
import {LocalSchedulingOp} from "@oplit/shared-module";

numeral.locale("fr");

export default defineComponent({
  name: "flux-data-body",
  components: {
    SectorsSelector,
    FDatePicker,
    FButton,
    FTextField,
    OPValidationModal,
    OperationPriorityChip,
    OperationTags,
  },
  props: {
    op: {
      type: Object as PropType<SchedulingOperation>,
      default: () => ({} as SchedulingOperation),
    },
    nextOp: {
      type: Object as PropType<SchedulingOperation | undefined>,
      default: undefined,
    },
    forceLoading: {type: Boolean, default: false},
    canMakeChange: {type: Boolean, default: false},
    zIndex: {type: Number, default: 2000},
    propagateOpsChanges: {type: Function, default: () => ({})},
  },
  setup(props) {
    const {t} = useI18n();
    const openSnackbar = inject<OpenSnackbarFunction>("openSnackbar");

    const schedulingStore = useSchedulingStore();
    const {
      sendSchedulingLoadEventToBackend,
      updateMultipleSchedulingTagsFromIds,
    } = schedulingStore;
    const {schedulingMachineCentersAndTags} = storeToRefs(schedulingStore);
    const {currentPermissions} = storeToRefs(usePermissionsStore());
    const {shiftPlanningItems} = storeToRefs(useParametersStore());
    const {operationComputedOpQuantityUnit} = useComputeQuantityUnit();

    const mainStore = useMainStore();
    const {apiClient, clientParameters, variables, stations} =
      storeToRefs(mainStore);
    const {getCtxNextAvailableDate, getCtxSectorOrderedCalendars} = mainStore;
    const {op} = toRefs(props);
    const {displayedOperationStatus} = useOperationStatus(op);
    const {globalTabsPerRoute, globallySelectedTab} = useGlobalTabs();

    const showValidationModal = ref(false);
    const selectedDate = ref<string>("");
    const loading = ref<boolean>(false);
    const tags = ref<string[]>([]);

    const canUpdateOperation = computed(() => {
      return props.canMakeChange && !isDoneOperation(props.op);
    });
    const canUpdateSector = computed(() => {
      if (!canUpdateOperation.value) return false;
      return currentPermissions.value.scheduling.update_of_machine;
    });
    const canUpdateDate = computed(() => {
      if (!canUpdateOperation.value) return false;
      return currentPermissions.value.scheduling.update_of_date;
    });
    /**
     * we allow adding tags/incidents for done operations
     */
    const canUpdateTags = computed(() => {
      if (!props.canMakeChange) return false;
      return currentPermissions.value.scheduling.create_update_of_tags;
    });

    const computedFastTrack = computed(
      () => getMergedSchedulingOp(props.op).fast_track,
    );

    const operationActionLabel = computed(() => {
      const {op_status} = props.op;

      switch (op_status) {
        case OF_STATUS.DONE:
          return t("scheduling.sidebar__op_data_body.action_restart_op");
        case OF_STATUS.ON_GOING:
          return t("scheduling.sidebar__op_data_body.action_close_op");
        default:
          return t("scheduling.sidebar__op_data_body.action_start_op");
      }
    });

    const displayedOPUnit = computed(() => {
      const {op: operation} = props;
      return operationComputedOpQuantityUnit(operation);
    });
    const displayedOPDuration = computed(() => {
      const {op} = props;

      const rawDurationValue = getOperationQuantity(
        op,
        clientParameters.value.has_gantt_load_adjustment,
      );
      const durationValue = numeral(+(rawDurationValue || 0)).format("0.[00]");
      const durationUnit = displayedOPUnit.value;

      return [durationValue, durationUnit].filter(Boolean).join(" ");
    });
    const displayedDate = computed<string>(() => {
      if (!selectedDate.value) return "-";

      const shift = getShiftFromDate(
        selectedDate.value,
        shiftPlanningItems.value,
      );

      return [new Date(selectedDate.value).toLocaleDateString(), shift?.name]
        .filter(Boolean)
        .join(" - ");
    });

    async function updateOperation(
      initialOperation: SchedulingOperation,
      update: Record<string, unknown>,
      should_return_insertion_results = false,
    ) {
      loading.value = true;
      const [result, error] = await sendSchedulingLoadEventToBackend({
        data: [{initial: initialOperation as LocalSchedulingOp, update}],
        should_return_updated_values: true,
        should_return_insertion_results,
        force_pending_order_recalculation: true,
      });
      if (error) {
        loading.value = false;
        return openSnackbar(null, "GENERIC_ERROR", error);
      }
      const mappedValues = pgOpsMapFn(result.updated_values, {
        keepOpsDone: true,
      });
      props.propagateOpsChanges(mappedValues);
      loading.value = false;
    }

    async function onOpTagAdd(newTags: SchedulingTag[]) {
      const tagIds = newTags.map((t) => t.id);
      await updateMultipleSchedulingTagsFromIds(tagIds);
      tags.value.push(...tagIds);
      const update = {op_tags: tags.value};
      await updateOperation(op.value, update);
    }

    async function onOpTagRemove(tag: string) {
      tags.value = tags.value.filter((t) => t !== tag);
      await updateMultipleSchedulingTagsFromIds([tag]);
      const update = {op_tags: tags.value};
      await updateOperation(op.value, update);
    }

    watch(
      () => props.op.op_tags,
      (new_tags) => (tags.value = new_tags),
    );

    return {
      OF_STATUS,
      status: ref<string[]>(Object.values(OF_STATUS)),
      faults: ref<string[]>([
        "none",
        "breakdown",
        "no-raw-material",
        "tools",
        "component-rupture",
        "quality",
        "other",
      ]),
      quantite: ref<number>(null),
      quantiteOF: ref<number>(null),
      uniteOF: ref<string>(null),
      selectedDate,
      loading,
      CSS_OPERATION_CARD_CLICK_OUTSIDE_CLASS,
      schedulingMachineCentersAndTags,
      operationComputedOpQuantityUnit,
      displayedOperationStatus,
      apiClient,
      clientParameters,
      variables,
      stations,
      getCtxNextAvailableDate,
      getCtxSectorOrderedCalendars,
      sendSchedulingLoadEventToBackend,
      getOFUnit,
      TEST_IDS,
      canUpdateSector,
      canUpdateDate,
      canUpdateTags,
      computedFastTrack,
      globalTabsPerRoute,
      globallySelectedTab,
      showValidationModal,
      operationActionLabel,
      displayedOPDuration,
      displayedDate,
      updateOperation,
      onOpTagAdd,
      onOpTagRemove,
    };
  },
  computed: {
    isDisabledQuantityProduced(): boolean {
      /**
       * we allow the modification of the quantity produced even in archived simulations
       * this condition used to be the below
       * return !this.canMakeChange
       */
      return false;
    },
    computedDate() {
      const debutPeriode = moment(periodeToText(this.selectedDate)).format(
        "YYYY-MM-DD",
      );
      return debutPeriode;
    },
    computedFault() {
      const {op, faults} = this;
      const fault = op?.new_fault ?? op?.fault;
      return faults.includes(fault) ? fault : "";
    },
    computedMachineCenter() {
      const {schedulingMachineCentersAndTags, computedSectorId} = this;
      const machineCenter = schedulingMachineCentersAndTags.find(
        (s: MachineCenter) => s.secteur_id === computedSectorId,
      );

      return machineCenter;
    },
    computedSectorId() {
      const {op} = this;
      return op?.new_secteur_id || op?.secteur_id;
    },
    computedProduced() {
      const {op: operation} = this;
      const quantityProduced = operation?.quantity_produced || 0;
      return +quantityProduced;
    },
    computedReject() {
      const {op: operation} = this;
      const reject = operation?.quantite_op_rebut || 0;
      return +reject;
    },
  },
  watch: {
    op: {
      immediate: true,
      handler: function () {
        this.loadData();
      },
    },
  },
  mounted() {
    setTimeout(() => {
      document.getElementById("scrollable-general")?.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
        inline: "start",
      });
    }, 100);
  },
  methods: {
    async setMachineCenter(machineCenter: MachineCenter) {
      if (this.loading) return;
      if (!machineCenter?.secteur_id) return;
      const update = {new_secteur_id: machineCenter.secteur_id};
      await this.updateOperation(this.op, update);

      this.$segment.value.track("[Ordo] Operation Sector Updated", {
        origin: "sidebar",
      });
    },
    async setFault(value: string) {
      const {computedFault} = this;
      if (this.loading) return;
      let update = {new_fault: value};
      if (computedFault === value || value === "none") update = {new_fault: ""};
      await this.updateOperation(this.op, update);

      this.$segment.value.track("[Ordo] Operation Incident Updated", {
        old_value: computedFault,
        new_value: update.new_fault,
      });
    },
    async setQttyProduced(value: string) {
      if (this.loading) return;
      const update: Record<string, string> = {quantity_produced: value};
      // QttyProduced can be greater than quantiteOF
      if (value >= this.quantiteOF) update.new_status = "done";

      await this.updateOperation(this.op, update);

      this.$segment.value.track("[Ordo] Operation Produced Quantity Updated", {
        old_value: this.computedProduced,
        new_value: value,
      });
    },

    handleContextualAction() {
      const {
        op: {op_status},
      } = this;

      if (op_status === OF_STATUS.ON_GOING) this.showValidationModal = true;
      else this.$emit("start-operation");
    },
    async setQuantities(quantityUpdateEvent: {
      addedProducedQuantity: number;
      addedRejectQuantity?: number;
      shouldCloseOP: boolean;
    }) {
      const {op, nextOp} = this;
      const {addedProducedQuantity, shouldCloseOP, addedRejectQuantity} =
        quantityUpdateEvent;

      if (this.loading) return;

      const currentOPUpdate: {
        quantity_produced?: number;
        quantite_op_rebut?: number;
        quantite_of_disponible?: number;
        new_status?: (typeof OF_STATUS)[keyof typeof OF_STATUS];
      } = {};
      const nextOPUpdate: Record<string, unknown> = {};

      // We handle:
      // - updating the current OP's quantity_produced
      // - updating the next OP's quantite_of_disponible
      if (addedProducedQuantity) {
        // Handling update on OP n
        const {quantity_produced: initialProducedQuantity} = op;
        currentOPUpdate.quantity_produced =
          (+initialProducedQuantity || 0) + (addedProducedQuantity || 0);

        // Handling update on OP n+1
        const {quantite_of_disponible: initialAvailableQuantity} = nextOp || {};
        const nextOPNewAvailableQuantity = Math.max(
          (+initialAvailableQuantity || 0) + (addedProducedQuantity || 0),
          0,
        );
        nextOPUpdate.quantite_of_disponible = nextOPNewAvailableQuantity;

        this.$segment.value.track("[Ordo] Quantity moved to operation", {
          origin: "sidebar",
          of_id: op.of_id,
          initial_op_id: op.op_id,
          target_op_id: nextOp?.op_id,
          quantity_produced_on_initial: currentOPUpdate.quantity_produced,
          quantity_of_disponible_on_target: nextOPUpdate.quantite_of_disponible,
          close_initial_op: shouldCloseOP,
        });
      }

      // Updating the current OP's quantite_op_rebut
      if (addedRejectQuantity) {
        const {quantite_op_rebut: initialRejectQuantity} = op;
        currentOPUpdate.quantite_op_rebut =
          (+initialRejectQuantity || 0) + (addedRejectQuantity || 0);

        this.$segment.value.track("[Ordo] Waste declared on operation", {
          origin: "sidebar",
          op_id: op.op_id,
          of_id: op.of_id,
          quantite_op_rebut: currentOPUpdate.quantite_op_rebut,
        });
      }

      if (shouldCloseOP) currentOPUpdate.new_status = OF_STATUS.DONE;
      else if ([OF_STATUS.POF, OF_STATUS.PENDING].includes(op.op_status))
        currentOPUpdate.new_status = OF_STATUS.ON_GOING;
      await this.updateOperation(this.op, currentOPUpdate);

      if (nextOp && nextOPUpdate.quantite_of_disponible) {
        if (
          [OF_STATUS.POF, OF_STATUS.PENDING, OF_STATUS.DONE].includes(
            nextOp.op_status,
          )
        )
          nextOPUpdate.new_status = OF_STATUS.AVAILABLE;
        await this.updateOperation(nextOp, nextOPUpdate);
      }

      this.showValidationModal = false;
    },

    async handleDateChange(date: string) {
      this.selectedDate = date;
    },
    async handleDateValidation() {
      const {
        clientParameters,
        op,
        getCtxSectorOrderedCalendars,
        getCtxNextAvailableDate,
      } = this;
      const {has_gantt_load_adjustment} = clientParameters;
      const {simulation_id, of_id, secteur_id, day_date, op_duration} = op;
      let update: {new_date?: string; first_day_duration?: number} = {};
      if (this.loading) return;

      const opSector = this.stations.find(({id}) => id === secteur_id);
      let nextAvailableDate = getCtxNextAvailableDate(
        this.selectedDate,
        false,
        opSector,
      );
      const isMovingRight = moment(day_date).isBefore(nextAvailableDate);
      let canAdjustLoadToCapa = has_gantt_load_adjustment && isMovingRight;
      if (canAdjustLoadToCapa) {
        const [, result] = await this.apiClient.getNextAvailableCapaDay({
          simulation_id,
          of_ids: [of_id],
          sector_id: secteur_id,
          new_date: nextAvailableDate,
          calendars: getCtxSectorOrderedCalendars(opSector),
          op_duration,
        });
        const {new_date, first_day_duration} = result;
        if (first_day_duration !== 0) {
          nextAvailableDate = new_date;
          update = {
            first_day_duration,
          };
        }
        canAdjustLoadToCapa = canAdjustLoadToCapa && first_day_duration !== 0;
      }
      update = {
        ...update,
        new_date: includeOriginalUpdateShiftPlanningInSiblingDate(
          this.selectedDate,
          nextAvailableDate,
        ),
      };

      //OPL-4456 : nouveau format des évènements d'ordo
      this.loading = true;
      const [result, error] = await this.sendSchedulingLoadEventToBackend({
        data: [{initial: this.op, update}],
        should_return_updated_values: true,
        should_return_insertion_results: true,
        ...(canAdjustLoadToCapa && {has_gantt_load_adjustment}),
      });
      if (error) {
        this.loading = false;
        return this.$openSnackbar(null, "GENERIC_ERROR");
      }
      const mappedValues = pgOpsMapFn(result.updated_values, {
        keepOpsDone: true,
      });
      this.propagateOpsChanges(mappedValues);
      this.loading = false;

      this.$segment.value.track("[Ordo] Operation Planned Date Updated", {
        origin: "sidebar",
      });
    },
    loadData() {
      let {op} = this;
      if (!op) return;
      this.selectedDate = op.date?.value ?? op.day_date;
      this.quantiteOF = null;
      this.uniteOF = null;

      //Update simulation data
      if (![null, undefined].includes(op?.quantite)) {
        this.quantite = Math.max(0, +op?.quantite || 0);
        this.quantiteOF = _.round(Math.max(0, +op?.quantite || 0), 2);
        this.uniteOF = op.unite;
      }
      if (op?.new_date) this.selectedDate = op?.new_date;
      if (![null, undefined].includes(op.quantite_of)) {
        this.uniteOF = getOFUnit(op);
        this.quantiteOF = _.round(op.quantite_of, 2);
      }
    },
    computeStatusColorText(status: string, isInModal = false) {
      let result = "";
      // FIXME
      if (status === "done") return "newDisableText";
      if (status == this.displayedOperationStatus.text || isInModal)
        result = schedulingStatusColorText(status);
      return result;
    },
    getOperationQuantity,
    getAssetURL,
  },
});
</script>

<style scoped lang="scss">
.flux-data-body__info-section {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 8px;
  width: 100%;
}

.flux-data-body__info-block {
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 48px;
  width: 100%;

  // DatePicker has inbuilt styles that we need to override
  :deep(.mx-datepicker) {
    svg {
      fill: none;
      width: 16px;
      height: 16px;
    }
  }
}

.flux-data-body__info-header {
  display: flex;
  flex-direction: column;
  align-items: start;
  justify-content: space-between;
}

.flux-data-body__info-header-title {
  display: flex;
  align-items: center;
  gap: 8px;
}

.flux-data-body__quantity-section {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 4px;
  width: 100%;
}

.flux-data-body__quantity-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
}

.flux-data-body__quantity-row-header {
  display: flex;
  align-items: center;
  gap: 4px;
}

.flux-data-body__quantity-row-input {
  :deep(input) {
    font-size: 0.875rem !important;
    text-align: right;
  }
}

.horizontal-line {
  display: inline-block;
  width: 1px;
  height: 40px;
  background-color: rgb(var(--v-theme-newSubText));
}
.incident-chip {
  height: 32px;
}
.incident-dropdown-chip {
  width: fit-content;
}
</style>
